import dayjs from "dayjs"
import { useShallow } from "zustand/react/shallow"
import { useLocation } from "react-router-dom"

// context
import useAppState from "context/hooks/useAppState"
import useAuthoringViewStore from "containers/studies/Study/subcomponents/Authoring/hooks/stores/useAuthoringViewStore"
import useAuthoringDataStore from "containers/studies/Study/subcomponents/Authoring/hooks/stores/useAuthoringDataStore"
import useAuthoringStore from "containers/studies/Study/subcomponents/Authoring/hooks/stores/useAuthoringStore"
import useChatbotStore from "context/stores/useChatbotStore"
import { useGetEnvVars } from "api/hooks/enum"

// utils
import useAuthoringValidationActions from "containers/studies/Study/subcomponents/Authoring/hooks/actions/useAuthoringValidationActions"

// apis
import { useGetAuthoringStudy, usePutAuthoringStudy, useGetAuthoringVersions } from "api/hooks/studies/useAuthoringApi"
import { useUserPermissions } from "api/hooks"

const useAuthoringSaveActions = () => {
  const { openToastNotification } = useAppState()
  const { data: envVars } = useGetEnvVars()
  const location = useLocation()
  const { authoringId, isUserLocked, setLastSaved, setShowSaveLock, enableAutoSave, setEnableAutoSave } =
    useAuthoringStore(
      useShallow((state) => ({
        authoringId: state.authoringId,
        isUserLocked: state.isUserLocked,
        enableAutoSave: state.enableAutoSave,
        setLastSaved: state.setLastSaved,
        setShowSaveLock: state.setShowSaveLock,
        setEnableAutoSave: state.setEnableAutoSave,
      }))
    )

  const { formData, forkedStudyData, overrideData, attachedDocuments, translationsData } = useAuthoringDataStore(
    useShallow((state) => ({
      formData: state.formData,
      forkedStudyData: state.forkedStudyData,
      overrideData: state.overrideData,
      attachedDocuments: state.attachedDocuments,
      translationsData: state.translationsData,
    }))
  )

  const { isReviewerView, showPagePreview, readOnly, setFormStatus } = useAuthoringViewStore(
    useShallow((state) => ({
      readOnly: state.readOnly,
      isReviewerView: state.isReviewerView,
      showPagePreview: state.showPagePreview,
      showPanel: state.showPanel,
      setFormStatus: state.setFormStatus,
    }))
  )

  const { setChatbotContext } = useChatbotStore(
    useShallow((state) => ({
      setChatbotContext: state.setChatbotContext,
    }))
  )

  const { data: authoring, refetch: refetchAuthoring } = useGetAuthoringStudy(authoringId, {
    ["disable-filter"]: !!isReviewerView,
  })
  const { refetch: refetchAuthoringVersions } = useGetAuthoringVersions(authoringId)
  const { data: permissions } = useUserPermissions()
  const putAuthoringStudy = usePutAuthoringStudy(authoringId)
  const { clearValidations } = useAuthoringValidationActions()

  const handleSubmit = (options = {}) => {
    if (
      permissions?.["study.edit"] &&
      isUserLocked === false &&
      isReviewerView !== true &&
      showPagePreview !== true &&
      formData &&
      enableAutoSave &&
      window?.location?.href?.includes("study-data") &&
      (readOnly === false || options?.is_major_version || options?.is_minor_version || options?.unlockOnSave) &&
      formData?.protocol?.study_type &&
      formData?.protocol?.protocol_id
    ) {
      options = { ...options } || {}
      const versionNotes = options?.versionNotes || null
      const unlockOnSave = options?.unlockOnSave

      // When auto-saving, sending formData in options.
      const _formData = options?.formData || formData
      const _forkedStudyData = options?.forkedStudyData || forkedStudyData
      const _overrideData = options?.overrideData || overrideData
      const _translationsData = options?.translationsData || translationsData

      if (authoring && formData && isReviewerView !== true && showPagePreview !== true) {
        let req = {
          study_id: authoring.study_id,
          study_data: {
            ..._formData,
            forked_study_data: _forkedStudyData,
            translations: _translationsData,
          },
          attached_documents: attachedDocuments,
          override_data: _overrideData,
        }

        if (unlockOnSave) {
          req.is_manual_save = unlockOnSave
          clearValidations()
        }
        setShowSaveLock(!unlockOnSave)

        // versioning
        if (options?.is_major_version) {
          setEnableAutoSave(false)
          setFormStatus("loading")
          req.is_major_version = true
        } else if (options?.is_minor_version) {
          setEnableAutoSave(false)
          setFormStatus("loading")
          req.is_minor_version = true
        }
        if (versionNotes) req.notes = versionNotes

        if (envVars?.ENABLE_STUDY_DATA_CHATBOT_WORKFLOW === true) {
          setChatbotContext({
            study: req,
            url: location.pathname,
          })
        }

        putAuthoringStudy(
          {
            id: `${authoring.id}/`,
            body: req,
          },
          {
            onSuccess: (res) => {
              // if (res?.error) console.log('ERROR', res.error);
              // if saved a new version, set new version in authoring var
              if (options?.is_major_version || options?.is_minor_version) {
                clearValidations()
                refetchAuthoringVersions()
                refetchAuthoring()
                if (options.is_major_version) {
                  openToastNotification("success", "Success", "Successfully saved major version")
                  setFormStatus("ready")
                } else if (options.is_minor_version) {
                  openToastNotification("success", "Success", "Successfully saved minor version")
                  setFormStatus("ready")
                }
              }
              setLastSaved(dayjs())
            },
          }
        )
      }
    }
  }

  return {
    handleSubmit,
  }
}

export default useAuthoringSaveActions
